'use client';

import React, { useState } from 'react';
import { X, Check, Zap, Crown, Loader2, LogIn, UserPlus } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface PremiumModalProps {
  open: boolean;
  onClose: () => void;
  triggerCategory?: string;
  trialMode?: boolean;
}

const FEATURES = [
  'Alle gesperrten Kategorien freischalten',
  'Spicy & Partychaos Inhalte',
  'Exklusive Fragen & Aufgaben',
  'Keine Werbung',
  'Neue Spiele zuerst',
];

export default function PremiumModal({ open, onClose, triggerCategory, trialMode }: PremiumModalProps) {
  const [selected, setSelected] = useState<'day' | 'week'>('week');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { user } = useAuth();
  const supabase = createClient();
  const router = useRouter();

  if (!open) return null;

  // --- AUTH GATE ---
  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <div
          className="relative w-full max-w-sm rounded-2xl p-6 animate-bounce-in overflow-hidden"
          style={{ background: 'var(--card)', border: '1px solid rgba(124,58,237,0.4)' }}
        >
          <div
            className="hero-glow-orb w-48 h-48 -top-20 -right-20 opacity-30"
            style={{ background: 'var(--primary)' }}
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/5 transition-colors z-10"
            style={{ color: 'var(--muted-foreground)' }}
          >
            <X size={18} />
          </button>

          <div className="flex justify-center mb-4">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center animate-pulse-glow"
              style={{ background: 'linear-gradient(135deg, var(--primary), #5B21B6)' }}
            >
              <Crown size={28} className="text-white" />
            </div>
          </div>

          <div className="text-center mb-6">
            <h2 className="font-extrabold text-2xl mb-2">
              <span className="shimmer-text">Premium freischalten</span>
            </h2>
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              Um Premium zu kaufen, musst du angemeldet sein.
            </p>
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold mt-3"
              style={{ background: 'rgba(74,222,128,0.12)', border: '1px solid rgba(74,222,128,0.3)', color: 'var(--neon-green)' }}
            >
              🎁 Neu registrieren &amp; 24h Premium gratis erhalten!
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => { onClose(); router.push('/sign-up-login-screen?mode=register'); }}
              className="w-full btn-accent flex items-center justify-center gap-2 py-3 font-bold text-sm rounded-xl"
            >
              <UserPlus size={16} />
              Jetzt kostenlos registrieren
            </button>
            <button
              onClick={() => { onClose(); router.push('/sign-up-login-screen?mode=login'); }}
              className="w-full flex items-center justify-center gap-2 py-3 font-semibold text-sm rounded-xl transition-all hover:bg-white/5"
              style={{ background: 'var(--muted)', border: '1px solid rgba(124,58,237,0.3)', color: 'var(--neon-violet)' }}
            >
              <LogIn size={16} />
              Bereits registriert? Anmelden
            </button>
          </div>

          <p className="text-center text-xs mt-4" style={{ color: 'var(--muted-foreground)' }}>
            Sichere Zahlung via Stripe · Jederzeit kündbar
          </p>
        </div>
      </div>
    );
  }
  // --- END AUTH GATE ---

  async function handleCheckout() {
    setLoading(true);
    setErrorMsg('');
    try {
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          plan: selected,
          userId: user?.id ?? null,
          userEmail: user?.email ?? null,
          successUrl: `${siteUrl}/profile?upgraded=1`,
          cancelUrl: `${siteUrl}/`,
        },
      });

      if (error) {
        // supabase-js wraps non-2xx responses — try to extract the actual error message
        let msg = 'Checkout konnte nicht gestartet werden.';
        try {
          // error.context may contain the raw Response object
          if (error.context instanceof Response) {
            const body = await error.context.json();
            msg = body?.error ?? msg;
          } else if (typeof error.context === 'object' && error.context?.error) {
            msg = error.context.error;
          } else if (error.message && !error.message.includes('non-2xx')) {
            msg = error.message;
          }
        } catch {
          msg = error.message ?? msg;
        }
        console.error('Checkout error:', error);
        setErrorMsg(msg);
        setLoading(false);
        return;
      }

      const url = (data as any)?.url;
      if (!url) {
        setErrorMsg('Checkout-URL fehlt. Bitte versuche es erneut.');
        setLoading(false);
        return;
      }

      // Use window.open to open Stripe Checkout in a new tab.
      // window.top.location.href throws a SecurityError when the app is embedded
      // in a cross-origin iframe (e.g. preview environments on a different domain).
      window.open(url, '_blank', 'noopener,noreferrer');
      setLoading(false);
    } catch (err: any) {
      console.error('Checkout exception:', err);
      setErrorMsg(err?.message ?? 'Ein Fehler ist aufgetreten.');
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full max-w-sm rounded-2xl p-6 animate-bounce-in overflow-hidden"
        style={{ background: 'var(--card)', border: '1px solid rgba(124,58,237,0.4)' }}
      >
        {/* Glow orb */}
        <div
          className="hero-glow-orb w-48 h-48 -top-20 -right-20 opacity-30"
          style={{ background: 'var(--primary)' }}
        />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-white/5 transition-colors z-10"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center animate-pulse-glow"
            style={{ background: 'linear-gradient(135deg, var(--primary), #5B21B6)' }}
          >
            <Crown size={28} className="text-white" />
          </div>
        </div>

        <div className="text-center mb-2">
          <h2 className="font-extrabold text-2xl mb-1">
            <span className="shimmer-text">Premium</span>
          </h2>
          {trialMode ? (
            <>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-2"
                style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.4)', color: 'var(--neon-amber)' }}
              >
                🎮 3 kostenlose Spiele gespielt
              </div>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Du hast deine <strong style={{ color: 'var(--foreground)' }}>3 kostenlosen Testspiele</strong> genutzt. Schalte Premium frei um unbegrenzt zu spielen!
              </p>
            </>
          ) : (
            <>
              {triggerCategory && (
                <p className="text-sm mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Die Kategorie <strong style={{ color: 'var(--neon-amber)' }}>{triggerCategory}</strong> ist nur mit Premium zugänglich.
                </p>
              )}
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                Schalte alle Inhalte frei und erlebe die volle Party-Power.
              </p>
            </>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 my-5">
          {FEATURES.map((feature) => (
            <li key={`premium-feat-${feature}`} className="flex items-center gap-2 text-sm">
              <Check size={14} style={{ color: 'var(--neon-green)' }} className="flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>

        {/* Plans */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          <button
            onClick={() => setSelected('day')}
            className="p-3 rounded-xl text-center transition-all"
            style={{
              border: `2px solid ${selected === 'day' ? 'var(--primary)' : 'var(--border)'}`,
              background: selected === 'day' ? 'rgba(124,58,237,0.1)' : 'var(--muted)',
            }}
          >
            <p className="font-bold text-lg">2,99 €</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pro Tag</p>
          </button>
          <button
            onClick={() => setSelected('week')}
            className="p-3 rounded-xl text-center transition-all relative"
            style={{
              border: `2px solid ${selected === 'week' ? 'var(--accent)' : 'var(--border)'}`,
              background: selected === 'week' ? 'rgba(245,158,11,0.1)' : 'var(--muted)',
            }}
          >
            <span
              className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}
            >
              Beliebt
            </span>
            <p className="font-bold text-lg">5,99 €</p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>pro Woche</p>
          </button>
        </div>

        {/* Error */}
        {errorMsg && (
          <p className="text-xs text-center mb-3" style={{ color: '#F87171' }}>
            {errorMsg}
          </p>
        )}

        {/* CTA */}
        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full btn-accent flex items-center justify-center gap-2 py-3 font-bold text-sm rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Zap size={16} />
          )}
          {loading ? 'Weiterleitung…' : `Jetzt freischalten — ${selected === 'day' ? '2,99 €' : '5,99 €'}`}
        </button>

        <p className="text-center text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
          Sichere Zahlung via Stripe · Jederzeit kündbar
        </p>
        <p className="text-center text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
          Oder{' '}
          <a href="/payment" className="underline hover:opacity-80 transition-opacity" style={{ color: 'var(--neon-violet)' }}>
            zur Zahlungsseite
          </a>
        </p>
      </div>
    </div>
  );
}